import { useState } from 'react';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { FeatureFlagUseCase } from '@ibk/shared-types';
import { TransactionFlow, TransactionType } from '../../enums';
import { useLazyGetPixScheduleReceiptQuery, useLazyGetV1PixReceiptQuery, useLazyGetV2PixReceiptQuery, useLazyGetV3PixReceiptQuery, } from '../../services/receiptApi';
import { mapPixReceiptFlow } from '../../utils/mapPixReceiptFlow';
function isSentOrReceivedOrReversalFlow(transactionFlow) {
    return (transactionFlow === TransactionFlow.CASH_IN ||
        transactionFlow === TransactionFlow.CASH_OUT ||
        transactionFlow === TransactionFlow.REVERSAL_IN ||
        transactionFlow === TransactionFlow.REVERSAL_OUT);
}
export function useGetPixReceipt() {
    const { isFeatureFlagWhiteListed } = useFeatureFlag(undefined, { autoLoad: false });
    const isBaasV2 = isFeatureFlagWhiteListed(FeatureFlagUseCase.CORE_BANKING_ENABLE_BAAS_V2);
    const [triggerV1] = useLazyGetV1PixReceiptQuery();
    const [triggerV2] = useLazyGetV2PixReceiptQuery();
    const [triggerV3] = useLazyGetV3PixReceiptQuery();
    const [triggerSchedule] = useLazyGetPixScheduleReceiptQuery();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [pixReceipt, setPixReceipt] = useState();
    async function getSchedulePixReceipt(id) {
        const { data } = await triggerSchedule({ id }, false);
        if (!data?.result) {
            setError(true);
        }
        else {
            setPixReceipt({
                type: TransactionType.PIX,
                flow: TransactionFlow.SCHEDULED,
                amount: data.result.amount,
                transactionAt: data.result.scheduledDate,
                description: data.result.description,
                destiny: {
                    name: data.result.beneficiaryName,
                    document: data.result.beneficiaryDocument,
                    pixKey: data.result.pix?.key,
                    bankName: data.result.pix?.bankName,
                    bankAccountNumber: data.result.pix?.bankAccountNumber,
                    bankAgency: data.result.pix?.bankAgency,
                },
                origin: {
                    name: data.result.pix?.originName,
                    document: data.result.pix?.originDocument,
                    bankName: data.result.pix?.originBankName,
                },
            });
        }
    }
    async function getSentOrReceivedPixReceipt(id, transactionFlow, instructionFlow) {
        let data;
        if (isBaasV2) {
            ({ data } = await triggerV3({ id, flow: mapPixReceiptFlow(transactionFlow) ?? '' }, false));
        }
        else if (instructionFlow) {
            ({ data } = await triggerV2({ id, instructionFlow }, false));
        }
        else {
            ({ data } = await triggerV1({ id, instructionFlow }, false));
        }
        if (!data?.result) {
            setError(true);
        }
        else {
            setPixReceipt(data.result);
        }
    }
    async function getPixReceipt(id, transactionFlow, instructionFlow) {
        setLoading(true);
        setError(false);
        setPixReceipt(undefined);
        try {
            if (transactionFlow === TransactionFlow.SCHEDULED) {
                await getSchedulePixReceipt(id);
            }
            else if (isSentOrReceivedOrReversalFlow(transactionFlow)) {
                await getSentOrReceivedPixReceipt(id, transactionFlow, instructionFlow ?? false);
            }
            else {
                setError(true);
            }
        }
        catch {
            setError(true);
        }
        finally {
            setLoading(false);
        }
    }
    return {
        loading,
        setLoading,
        error,
        setError,
        pixReceipt,
        setPixReceipt,
        getPixReceipt,
    };
}
